import { useEffect } from "react";

/**
 * Hold a screen wake lock while `active` is true, so the phone doesn't dim /
 * lock mid-question (a real annoyance on long reading passages).
 *
 * The OS releases a wake lock automatically whenever the page is hidden
 * (tab switch, lock button), so we re-acquire on `visibilitychange` when the
 * page comes back to the foreground. Released on unmount / when `active`
 * goes false (navigating away from a solving surface) so we don't pin the
 * screen awake across the whole app.
 *
 * Screen Wake Lock is supported on iOS Safari 16.4+ and modern Android
 * Chrome. Anywhere it's missing (or a request is refused), this is a silent
 * no-op — never throws.
 */
export function useWakeLock(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const nav = navigator as Navigator & {
      wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
    };
    if (!nav.wakeLock) return;

    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || sentinel || document.visibilityState !== "visible") return;
      try {
        sentinel = await nav.wakeLock!.request("screen");
        // If the OS drops it (e.g. backgrounding), clear our handle so the
        // visibilitychange handler can re-acquire.
        sentinel.addEventListener?.("release", () => {
          sentinel = null;
        });
      } catch {
        // Unsupported, not visible, or refused — leave the screen to the OS.
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") void acquire();
    };

    void acquire();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      try {
        void sentinel?.release?.();
      } catch {
        /* already released */
      }
      sentinel = null;
    };
  }, [active]);
}

/** Minimal shape of WakeLockSentinel (avoids depending on lib.dom typings). */
interface WakeLockSentinelLike {
  release?: () => Promise<void>;
  addEventListener?: (type: "release", listener: () => void) => void;
}
